import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, CheckCircle2, XCircle, Clock, Filter, RefreshCw,
  Download, FileText,
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OUTCOME = {
  pending: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock, label: 'Pending review' },
  approved: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2, label: 'Approved' },
  rejected: { color: 'bg-rose-100 text-rose-700 border-rose-200', icon: XCircle, label: 'Rejected' },
};

const ENTITY_LABEL = {
  expense: 'Expense',
  payment: 'Payment',
  budget_category: 'Budget Category',
  invoice: 'Invoice',
  kpi: 'KPI',
};

/**
 * SocialActorAuditLog — append-only audit trail of every change a Social
 * Actor proposes on a project, visible in full to Admins / PMs.
 *
 * Renders inline in a project's detail view as a tab. Social Actors see
 * only their own rows; admin/PM see everyone.
 */
const SocialActorAuditLog = ({ projectId, currentUser }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    []
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const params = {};
        if (outcomeFilter !== 'all') params.outcome = outcomeFilter;
        if (entityFilter !== 'all') params.entity_type = entityFilter;
        const { data } = await axios.get(
          `${API}/projects/${projectId}/social-actor-audit`,
          { headers, params }
        );
        if (!cancelled) setItems(data || []);
      } catch (err) {
        if (!cancelled) {
          if (err?.response?.status === 403) {
            setItems([]);
          } else {
            toast.error('Failed to load audit log');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [projectId, headers, outcomeFilter, entityFilter]);

  const counts = useMemo(() => ({
    total: items.length,
    pending: items.filter((i) => i.outcome === 'pending').length,
    approved: items.filter((i) => i.outcome === 'approved').length,
    rejected: items.filter((i) => i.outcome === 'rejected').length,
  }), [items]);

  /**
   * Stream the export through fetch + Blob so the browser issues a normal
   * "Save as…" dialog. The request inherits the active outcome + entity
   * filters so the downloaded file matches the on-screen view.
   */
  const downloadExport = async (format) => {
    try {
      const qs = new URLSearchParams({ format });
      if (outcomeFilter !== 'all') qs.set('outcome', outcomeFilter);
      if (entityFilter !== 'all') qs.set('entity_type', entityFilter);
      const res = await fetch(
        `${API}/projects/${projectId}/social-actor-audit/export?${qs}`,
        { headers }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m?.[1] || `audit.${format === 'pdf' ? 'pdf' : 'csv'}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${items.length} row(s) as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  const renderRow = (it) => {
    const conf = OUTCOME[it.outcome] || OUTCOME.pending;
    const Icon = conf.icon;
    const at = it.created_at ? new Date(it.created_at).toLocaleString('en-GB') : '—';
    const reviewed = it.reviewed_at ? new Date(it.reviewed_at).toLocaleString('en-GB') : null;
    const amt = it.payload_snapshot?.amount;
    return (
      <div key={it.id} className="border rounded-lg p-3 flex gap-3 hover:bg-slate-50 transition"
           data-testid={`audit-row-${it.id}`}>
        <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center ${conf.color}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-medium text-slate-900 truncate">
              {it.actor_username}
              <span className="text-slate-500 font-normal"> · {it.action} </span>
              <span className="font-semibold">{ENTITY_LABEL[it.entity_type] || it.entity_type}</span>
              {it.entity_label && (
                <span className="text-slate-500 font-normal"> · &ldquo;{it.entity_label}&rdquo;</span>
              )}
            </div>
            <Badge variant="outline" className={`text-xs ${conf.color}`}>{conf.label}</Badge>
          </div>
          <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-3">
            <span>{at}</span>
            {amt != null && (
              <span>Amount: <span className="font-semibold text-slate-700">
                €{Number(amt).toLocaleString('el-GR')}
              </span></span>
            )}
            {it.payload_snapshot?.category && (
              <span>Category: <span className="text-slate-700">{it.payload_snapshot.category}</span></span>
            )}
            {it.payload_snapshot?.summary && (
              <span className="w-full text-slate-700">Changes: <span className="font-medium">{it.payload_snapshot.summary}</span></span>
            )}
            {reviewed && it.reviewer_username && (
              <span>
                Reviewed by <span className="font-semibold text-slate-700">{it.reviewer_username}</span> · {reviewed}
              </span>
            )}
          </div>
          {it.notes && (
            <p className="text-xs italic text-slate-600 mt-1 bg-slate-100 rounded px-2 py-1">
              &ldquo;{it.notes}&rdquo;
            </p>
          )}
        </div>
      </div>
    );
  };

  const canSeeAll = currentUser?.role === 'administrator' || currentUser?.role === 'project_manager';

  return (
    <div className="space-y-4" data-testid="social-actor-audit-log">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" /> Social Actor Audit Log
          </h2>
          <p className="text-sm text-slate-500">
            {canSeeAll
              ? 'Append-only trail of every Social Actor proposal — approved, rejected or still pending.'
              : 'Your activity on this project — all proposals submitted, approved or rejected.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
            <SelectTrigger className="w-44" data-testid="audit-outcome-filter">
              <Filter className="h-3 w-3 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All outcomes</SelectItem>
              <SelectItem value="pending">Pending review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-44" data-testid="audit-entity-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              <SelectItem value="expense">Expenses</SelectItem>
              <SelectItem value="payment">Payments</SelectItem>
              <SelectItem value="budget_category">Budget categories</SelectItem>
              <SelectItem value="invoice">Invoices</SelectItem>
              <SelectItem value="kpi">KPIs</SelectItem>
            </SelectContent>
          </Select>
          {/* Export: respects current outcome + entity filter so the file
              matches what the user is looking at. */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadExport('csv')}
            data-testid="export-audit-csv"
            disabled={items.length === 0}
          >
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadExport('pdf')}
            data-testid="export-audit-pdf"
            disabled={items.length === 0}
          >
            <FileText className="h-4 w-4 mr-1" /> PDF
          </Button>
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Card><CardContent className="p-3">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Total</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{counts.total}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-xs text-amber-700 uppercase tracking-wider">Pending</div>
          <div className="text-2xl font-bold text-amber-700 mt-1">{counts.pending}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-xs text-emerald-700 uppercase tracking-wider">Approved</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">{counts.approved}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-xs text-rose-700 uppercase tracking-wider">Rejected</div>
          <div className="text-2xl font-bold text-rose-700 mt-1">{counts.rejected}</div>
        </CardContent></Card>
      </div>

      {/* Rows */}
      {loading ? (
        <div className="py-12 text-center text-slate-500" data-testid="audit-loading">Loading…</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <Activity className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">
              No Social Actor activity recorded for this project yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">{items.map(renderRow)}</div>
      )}
    </div>
  );
};

export default SocialActorAuditLog;
